import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, dateFmt, statusMeta, hasRole, formatErr } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, FileDown, ClipboardList, Search, Archive, ArchiveRestore } from "lucide-react";

const STATUSES = ["open", "partially_invoiced", "closed"];

export default function PurchaseOrders() {
    const { user } = useAuth();
    const canArchive = hasRole(user, ["school_admin", "purchasing_admin"]);
    const [rows, setRows] = useState(null);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [archived, setArchived] = useState(false);
    const [busy, setBusy] = useState("");

    const load = () => {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (status !== "all") p.set("status", status);
        if (archived) p.set("archived", "true");
        api.get(`/purchase-orders?${p.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    };
    useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, status, archived]);

    const viewPdf = async (po) => {
        setBusy(po.id);
        try {
            const res = await api.get(`/purchase-orders/${po.id}/pdf`, { responseType: "blob" });
            window.open(URL.createObjectURL(res.data), "_blank");
        } catch { toast.error("Could not open PO PDF"); } finally { setBusy(""); }
    };
    const toggleArchive = async (po) => {
        try { await api.post(`/purchase-orders/${po.id}/archive`, { archived: !archived }); toast.success(archived ? "Restored" : "Archived"); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    return (
        <div className="space-y-6 pe-fade-up">
            <div>
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald">Purchase Orders</h1>
                <p className="text-muted-foreground text-sm mt-1">Approved and issued purchase orders.</p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PO # or vendor" className="pl-9" data-testid="po-search" />
                </div>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-[180px]" data-testid="po-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusMeta(s).label}</SelectItem>)}
                    </SelectContent>
                </Select>
                {canArchive && (
                    <Button variant={archived ? "default" : "outline"} className="rounded-lg" onClick={() => setArchived((v) => !v)} data-testid="po-archived-toggle">
                        <Archive className="h-4 w-4 mr-1" /> {archived ? "Viewing archived" : "Show archived"}
                    </Button>
                )}
            </div>

            <div className="bg-white border border-border rounded-xl overflow-hidden">
                {rows === null ? (
                    <div className="grid place-items-center py-16 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
                ) : rows.length === 0 ? (
                    <div className="py-16 text-center text-muted-foreground"><ClipboardList className="h-8 w-8 mx-auto mb-3 opacity-40" /> No purchase orders found.</div>
                ) : (
                    <div className="overflow-x-auto pe-scroll">
                        <table className="w-full text-sm" data-testid="po-table">
                            <thead><tr className="text-left text-muted-foreground border-b border-border bg-secondary/40">
                                <th className="p-3 font-medium">PO #</th>
                                <th className="p-3 font-medium">Vendor</th>
                                <th className="p-3 font-medium">Date</th>
                                <th className="p-3 font-medium text-right">Total</th>
                                <th className="p-3 font-medium text-right">Invoiced</th>
                                <th className="p-3 font-medium">Status</th>
                                <th className="p-3 font-medium text-right">Actions</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((po) => (
                                    <tr key={po.id} className="border-b border-border last:border-0 hover:bg-secondary/40">
                                        <td className="p-3 font-mono text-brand-emerald">{po.po_number}</td>
                                        <td className="p-3">{po.vendor_name || "—"}</td>
                                        <td className="p-3 text-muted-foreground">{dateFmt(po.created_at)}</td>
                                        <td className="p-3 text-right font-mono">{money(po.total)}</td>
                                        <td className="p-3 text-right font-mono text-muted-foreground">{money(po.invoiced_total)}</td>
                                        <td className="p-3"><StatusBadge status={po.status} /></td>
                                        <td className="p-3 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button variant="outline" size="sm" className="rounded-lg" disabled={busy === po.id} onClick={() => viewPdf(po)} data-testid={`po-pdf-${po.id}`}>
                                                    {busy === po.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                                                </Button>
                                                {canArchive && (
                                                    <Button variant="ghost" size="sm" onClick={() => toggleArchive(po)} data-testid={`archive-po-${po.id}`}>
                                                        {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
